import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error boundary component to catch and display React errors
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-2xl w-full space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Es ist ein Fehler aufgetreten</AlertTitle>
              <AlertDescription className="space-y-4">
                <p>
                  Die Anwendung ist auf einen unerwarteten Fehler gestoßen. Bitte laden Sie die Seite neu
                  oder versuchen Sie es später erneut.
                </p>
                {this.state.error && (
                  <details className="mt-4">
                    <summary className="cursor-pointer font-medium">
                      Fehlerdetails anzeigen
                    </summary>
                    <div className="mt-2 p-4 bg-muted rounded-md">
                      <p className="font-mono text-sm break-all">
                        {this.state.error.toString()}
                      </p>
                      {this.state.errorInfo && (
                        <pre className="mt-2 text-xs overflow-auto max-h-64">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      )}
                    </div>
                  </details>
                )}
                <div className="flex gap-2 mt-4">
                  <Button onClick={this.handleReset} variant="outline">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Zurücksetzen
                  </Button>
                  <Button onClick={() => window.location.href = '/'}>
                    Zur Startseite
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
